// GlimtSend — fan sender ephemeral glimt (tekst / bilde / video) til en creator.
// Mobil-optimert. Bruker samme kamera + editor som feed-glimt for bilde/video.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Send, Camera, Type, Ban } from "lucide-react";
import api from "../lib/api";
import GlimtCamera from "../components/GlimtCamera";
import { isMobileDevice } from "../lib/isMobileDevice";
import { toast } from "sonner";

export default function GlimtSend() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("choose"); // choose | text | camera | preview
  const [capture, setCapture] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    api.get(`/creators/${username}/dm-status`).then(r => setStatus(r.data)).catch(() => {
      setStatus({ can_send: false, reason: "error" });
    });
  }, [username]);

  useEffect(() => {
    if (!capture?.blob) { setPreviewUrl(""); return; }
    const u = URL.createObjectURL(capture.blob);
    setPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [capture]);

  if (!status) return <div className="min-h-screen bg-black flex items-center justify-center text-[var(--cream-soft)]">Laster…</div>;

  if (!status.can_send) {
    const reasons = {
      not_creator: "Fans kan kun sende glimt til skapere. Fan↔fan er ikke støttet.",
      not_friends: "Dere må være Glimt-venner før dere kan sende glimt. Legg til hverandre først.",
      self: "Du kan ikke sende glimt til deg selv.",
      closed: "Mottakeren mottar ikke glimt for øyeblikket.",
      blocked: "Du kan ikke sende glimt til denne brukeren.",
      rate_limited: "Du har nådd dagsgrensen. Prøv i morgen.",
      error: "Kunne ikke sjekke om mottakeren tar imot glimt.",
    };
    return (
      <div className="min-h-screen bg-black flex flex-col" data-testid="glimt-send-blocked">
        <div className="p-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-[var(--cream)] p-2"><X /></button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-3">
          <Ban className="w-10 h-10 text-[var(--cream-faint)]" />
          <p className="text-[var(--cream)]">{reasons[status.reason] || "Kan ikke sende akkurat nå."}</p>
        </div>
      </div>
    );
  }

  const a = status.accepts || {};

  const doSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("recipient_username", username);
      if (capture) {
        const isVid = capture.type === "video";
        fd.append("file", capture.blob, isVid ? "glimt.mp4" : "glimt.jpg");
        fd.append("message", text.slice(0, 500));
      } else {
        fd.append("message", text.slice(0, 500));
      }
      await api.post("/glimt-dm/send", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sendt");
      navigate(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally { setSending(false); }
  };

  if (mode === "camera") {
    if (!isMobileDevice()) {
      return <div className="min-h-screen bg-black flex items-center justify-center text-white p-8 text-center">Kamera kun på mobil.</div>;
    }
    return (
      <GlimtCamera
        onClose={() => setMode("choose")}
        onCapture={(c) => { setCapture(c); setMode("preview"); }}
      />
    );
  }

  if (mode === "text") {
    return (
      <div className="min-h-screen bg-black flex flex-col" data-testid="glimt-send-text">
        <div className="p-4 flex items-center justify-between">
          <button onClick={() => setMode("choose")} className="text-[var(--cream)] p-2"><X /></button>
          <div className="text-[var(--cream)] text-sm">Til @{username}</div>
          <button onClick={doSend} disabled={sending || !text.trim()}
            className="px-4 py-2 rounded-full bg-[var(--magenta)] text-black text-sm font-bold disabled:opacity-40"
            data-testid="glimt-send-text-submit">
            <Send className="w-4 h-4 inline" />
          </button>
        </div>
        <textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Skriv tekst…"
          className="flex-1 bg-transparent border-0 outline-none text-[var(--cream)] text-lg px-6 py-4 resize-none"
          data-testid="glimt-send-text-input"
        />
        {status.ephemeral === "on_open" && (
          <div className="p-3 text-center text-[var(--cream-faint)] text-xs">Slettes rett etter skaper åpner.</div>
        )}
        {status.ephemeral === "24h" && (
          <div className="p-3 text-center text-[var(--cream-faint)] text-xs">Slettes 24 t etter skaper åpner.</div>
        )}
      </div>
    );
  }

  if (mode === "preview" && capture) {
    return (
      <div className="min-h-screen bg-black flex flex-col" data-testid="glimt-send-preview">
        <div className="relative flex-1">
          {capture.type === "image" ? (
            <img src={previewUrl} alt="" className="absolute inset-0 w-full h-full object-contain" />
          ) : (
            <video src={previewUrl} className="absolute inset-0 w-full h-full object-contain" autoPlay playsInline muted loop />
          )}
          <button onClick={() => { setCapture(null); setMode("choose"); }} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white"><X /></button>
        </div>
        <div className="p-4 bg-black/90 space-y-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Legg til melding (valgfritt)"
            className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-[var(--cream)] outline-none"
            maxLength={500}
            data-testid="glimt-send-caption"
          />
          <button
            onClick={doSend}
            disabled={sending}
            className="w-full h-12 rounded-full bg-[var(--magenta)] text-black font-bold flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="glimt-send-media-submit"
          >
            {sending ? "Sender…" : <><Send className="w-4 h-4" /> Send til @{username}</>}
          </button>
        </div>
      </div>
    );
  }

  // choose-mode
  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="glimt-send-choose">
      <div className="p-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-[var(--cream)] p-2"><X /></button>
        <div className="text-[var(--cream)] text-sm">Send til @{username}</div>
        <div className="w-8" />
      </div>
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
        <p className="text-[var(--cream-soft)] text-sm text-center max-w-xs">
          Skaperen tar imot: {[
            a.tekst && "tekst",
            a.bilde && "bilde",
            a.video && "video",
          ].filter(Boolean).join(", ") || "ingenting"}
        </p>
        {a.tekst && (
          <button
            onClick={() => setMode("text")}
            className="w-full max-w-xs h-14 rounded-full bg-white/10 hover:bg-white/15 text-[var(--cream)] flex items-center justify-center gap-3"
            data-testid="glimt-send-choose-text"
          >
            <Type className="w-5 h-5" /> Skriv tekst
          </button>
        )}
        {(a.bilde || a.video) && isMobileDevice() && (
          <button
            onClick={() => setMode("camera")}
            className="w-full max-w-xs h-14 rounded-full bg-[var(--magenta)] text-black font-bold flex items-center justify-center gap-3"
            data-testid="glimt-send-choose-camera"
          >
            <Camera className="w-5 h-5" /> {a.bilde && a.video ? "Bilde / Video" : a.bilde ? "Ta bilde" : "Ta video"}
          </button>
        )}
        {(a.bilde || a.video) && !isMobileDevice() && (
          <p className="text-[var(--cream-faint)] text-xs text-center max-w-xs">Bilde/video krever mobil.</p>
        )}
      </div>
    </div>
  );
}
